// /platform/email — the platform's own outbound mailbox (plan §7, §9).
//
// This is the single `scope = 'PLATFORM'` row. Everything Orio360 itself sends
// runs through it — login OTPs, staff invites, tenant lifecycle mail and the
// delivery-health alerts we send owners about THEIR broken accounts — which
// makes it the one mailbox that must never be down. Tenant business mail
// (invoices, re-order links, PO/RFQ) stays on each tenant's own account and
// never falls back here.
//
// SUPER_ADMIN writes. ADMIN reads (the route gate is level 80); the controller
// enforces the same split, the UI only decides whether to render read-only.

import { useCallback, useEffect, useState } from 'react';
import { PageContainer, Button, Skeleton, ErrorBoundary, useToast } from '@bbpos/ui';
import { platformEmailService } from '@/services/platform-email.service';
import { EmailHealthBanner } from '@/components/email/EmailHealthBanner';
import { EmailStatusPill } from '@/components/email/EmailStatusPill';
import { EmailTransportForm } from '@/components/email/EmailTransportForm';
import { EmailVerificationCard } from '@/components/email/EmailVerificationCard';
import { SendTestEmailCard } from '@/components/email/SendTestEmailCard';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FORM, cn } from '@/constants/design-tokens';
import type { EmailTransportConfigMasked, UpsertEmailTransportInput } from '@bbpos/types';
import { SMTP_PROVIDER_LABELS } from '@bbpos/types';

function formatStamp(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString('en-IN');
}

export function PlatformEmailPage() {
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.role ?? s.profile?.role ?? '');
  const loginEmail = useAuthStore((s) => s.user?.email ?? s.profile?.email ?? '');
  const canEdit = role === 'SUPER_ADMIN';

  const [config, setConfig] = useState<EmailTransportConfigMasked | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disabling, setDisabling] = useState(false);
  // Expiry of the code this session just triggered — the GET shape carries
  // `verification_sent_at` but not the TTL, so it only exists in memory.
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setConfig(await platformEmailService.getConfig());
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        setConfig(null);
      } else {
        toast({
          variant: 'error',
          title: 'Failed to load the platform account',
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    document.title = 'Platform Email — Orio360 Admin';
  }, []);

  const handleSave = async (input: UpsertEmailTransportInput) => {
    setSaving(true);
    setSaveError(null);
    try {
      const { config: saved, verification } = await platformEmailService.upsertConfig(input);
      setConfig(saved);
      if (verification.sent) {
        setExpiresAt(verification.expires_at ?? null);
        toast({
          variant: 'success',
          title: 'Verification code sent',
          description: `Check ${verification.sent_to ?? saved.email} for a 6-digit code.`,
        });
      } else {
        setExpiresAt(null);
        const message = verification.error ?? 'The mail server rejected these credentials.';
        setSaveError(message);
        toast({ variant: 'warning', title: 'Could not connect', description: message });
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Save failed';
      setSaveError(message);
      toast({ variant: 'error', title: 'Could not save', description: message });
    } finally {
      setSaving(false);
    }
  };

  const handleVerified = (verified: EmailTransportConfigMasked) => {
    setConfig(verified);
    setExpiresAt(null);
    setSaveError(null);
    toast({
      variant: 'success',
      title: 'Platform sender verified',
      description: `OTPs, invites and lifecycle mail now go out from ${verified.email}.`,
    });
  };

  const handleDisable = async () => {
    if (
      !window.confirm(
        'Disable the platform sending account? Login OTPs, staff invites and all lifecycle email stop going out until an account is verified again.',
      )
    ) {
      return;
    }
    setDisabling(true);
    try {
      await platformEmailService.disableConfig();
      toast({ variant: 'success', title: 'Platform sending account disabled' });
      setExpiresAt(null);
      await load();
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not disable',
        description: err instanceof ApiError ? err.message : 'Please try again',
      });
    } finally {
      setDisabling(false);
    }
  };

  // A PENDING row whose handshake failed has no code out there to enter
  // (`verification_sent_at` stays NULL — plan §5.2), so it gets the form and
  // the error, not the code box.
  const isPending =
    config?.verification_status === 'PENDING' && Boolean(config.verification_sent_at);
  const isVerified = config?.verification_status === 'VERIFIED';
  // No verified platform row means no OTP mail at all — say how to get in.
  const showBootstrapHint = !loading && !isVerified;

  return (
    <ErrorBoundary>
      <PageContainer>
        {/* Hero */}
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-violet-600 via-indigo-600 to-brand-600 p-6 text-white shadow-[0_20px_60px_-20px_rgba(124,58,237,0.55)] animate-fade-in">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-violet-100">
                Platform · Outbound mail
              </div>
              <h1 className="text-2xl font-bold text-white">Platform Email</h1>
              <p className="mt-1 max-w-2xl text-sm text-violet-100">
                Orio360&apos;s own mailbox. Login OTPs, staff invites, tenant welcome and trial /
                grace / cancellation mail, and the alerts we send owners when their own sending
                account breaks — all go out from here, whichever tenant the recipient belongs to.
              </p>
            </div>
            <EmailGlyph />
          </div>
        </div>

        {/* SUPER_ADMIN-only write notice */}
        {!canEdit && (
          <div
            className={cn(GLASS, 'mb-6 flex items-start gap-3 border-l-4 border-brand-500 p-4')}
            data-testid="platform-email-readonly-banner"
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-500 text-xs font-bold text-white">
              i
            </div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white">Read-only</div>
              You have read-only access to the platform sending account. Only a{' '}
              <span className="font-semibold">SUPER_ADMIN</span> can save, verify, test or disable
              it.
            </div>
          </div>
        )}

        {/* Bootstrap: without a verified platform account there is no OTP mail */}
        {showBootstrapHint && (
          <div
            className={cn(GLASS, 'mb-6 flex items-start gap-3 border-l-4 border-warning-500 p-4')}
            data-testid="platform-email-bootstrap-hint"
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-warning-500 text-xs font-bold text-white">
              !
            </div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white">
                Until this is verified, login OTP emails are not delivered
              </div>
              Sign in with the DEV-mode OTP <span className="font-mono">123456</span> (
              <span className="font-mono">ENVIRONMENT=DEV</span>) or with password login while you
              set this up. On a fresh environment that is the only way in — there is no env-var
              seed.
            </div>
          </div>
        )}

        {/* FAILING is the one state that must interrupt — it sits above everything */}
        <EmailHealthBanner
          config={config}
          client={platformEmailService}
          canEdit={canEdit}
          onRechecked={load}
        />

        {/* Status cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <StatusCard config={config} loading={loading} />
          <SenderCard config={config} loading={loading} />
          <HealthCard config={config} loading={loading} />
          <LastTestCard config={config} loading={loading} />
        </div>

        {loading ? (
          <Skeleton className="h-64" />
        ) : (
          <div className="space-y-6">
            {isPending && config && (
              <EmailVerificationCard
                config={config}
                client={platformEmailService}
                canEdit={canEdit}
                expiresAt={expiresAt}
                onVerified={handleVerified}
              />
            )}

            {isVerified && config && (
              <SendTestEmailCard
                config={config}
                client={platformEmailService}
                canEdit={canEdit}
                defaultTo={loginEmail}
                onTested={load}
              />
            )}

            {saveError && (
              <p
                className="rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:bg-danger-900/30 dark:text-danger-300"
                data-testid="platform-email-save-error"
              >
                {saveError}
              </p>
            )}

            <EmailTransportForm
              existing={config}
              canEdit={canEdit}
              saving={saving}
              onSubmit={handleSave}
            />

            {config && config.verification_status !== 'DISABLED' && canEdit && (
              <div className={cn(GLASS_FORM, 'flex flex-wrap items-center gap-3 p-5')}>
                <div className="mr-auto text-sm text-neutral-600 dark:text-neutral-300">
                  <div className="font-semibold text-neutral-900 dark:text-white">
                    Stop using this account
                  </div>
                  Every platform mail — including login OTPs — stops until another account is
                  verified.
                </div>
                <Button variant="secondary" onClick={handleDisable} loading={disabling}>
                  Disable sending
                </Button>
              </div>
            )}
          </div>
        )}

        {/* What runs through this account */}
        <div className={cn(GLASS, 'mt-6 border-l-4 border-indigo-500 p-5')}>
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-500 text-xs font-bold text-white">
              OUT
            </div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="mb-2 font-semibold text-neutral-900 dark:text-white">
                What this account sends
              </div>
              <ul className="list-inside list-disc space-y-1">
                <li>Login OTP codes and staff invitations, for every tenant.</li>
                <li>Tenant lifecycle mail — provisioned, trial reminders, grace, cancellation.</li>
                <li>
                  Delivery-health alerts telling an OWNER their own sending account is failing.
                </li>
                <li>
                  Not tenant business mail: invoices, re-order links, purchase orders and RFQs go
                  out on each tenant&apos;s own account, and never fall back to this one.
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Security notice */}
        <div className={cn(GLASS, 'mt-4 border-l-4 border-success-500 p-5')}>
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-success-500 text-xs font-bold text-white">
              SEC
            </div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="mb-1 font-semibold text-neutral-900 dark:text-white">
                Encrypted, and never plaintext on the wire
              </div>
              The password is AES-256-GCM encrypted at rest and never returned by the API. Every
              connection uses implicit TLS or mandatory STARTTLS, and the account is re-checked
              daily at 5:00 AM IST.
            </div>
          </div>
        </div>
      </PageContainer>
    </ErrorBoundary>
  );
}

// ─── Cards ────────────────────────────────────────────────────────────────

function CardShell({
  icon,
  gradient,
  label,
  children,
}: {
  icon: string;
  gradient: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(GLASS, 'p-5 transition-all duration-200 hover:shadow-lg')}>
      <div className="mb-3 flex items-center gap-3">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br text-xs font-bold tracking-wider text-white',
            gradient,
          )}
          aria-hidden="true"
        >
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</span>
      </div>
      {children}
    </div>
  );
}

function StatusCard({
  config,
  loading,
}: {
  config: EmailTransportConfigMasked | null;
  loading: boolean;
}) {
  const gradient = !config
    ? 'from-neutral-400 to-neutral-500'
    : config.verification_status === 'VERIFIED'
      ? 'from-success-500 to-teal-500'
      : config.verification_status === 'PENDING'
        ? 'from-warning-500 to-amber-500'
        : 'from-neutral-400 to-neutral-500';
  return (
    <CardShell icon="AUTH" gradient={gradient} label="Status">
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : (
        <div className="space-y-2">
          <EmailStatusPill config={config} />
          {config ? (
            <>
              <div className="font-mono text-xs text-neutral-500 dark:text-neutral-400">
                {SMTP_PROVIDER_LABELS[config.provider]} · {config.smtp_host}:{config.smtp_port}
              </div>
              <div className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
                Updated {formatStamp(config.updated_at) ?? '—'}
              </div>
            </>
          ) : (
            <div className="text-xs text-neutral-500 dark:text-neutral-400">
              No platform account yet. Nothing Orio360 authors — OTP, invites, lifecycle mail — can
              be delivered until one is verified.
            </div>
          )}
        </div>
      )}
    </CardShell>
  );
}

function SenderCard({
  config,
  loading,
}: {
  config: EmailTransportConfigMasked | null;
  loading: boolean;
}) {
  return (
    <CardShell icon="FROM" gradient="from-violet-500 to-pink-500" label="Sender">
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : config ? (
        <>
          <div className="mb-2 break-all rounded-md bg-neutral-50 p-2 font-mono text-sm text-neutral-800 dark:bg-neutral-800/60 dark:text-neutral-200">
            {config.from_name ? `${config.from_name} <${config.email}>` : config.email}
          </div>
          {config.reply_to_email && (
            <div className="break-all font-mono text-xs text-neutral-500 dark:text-neutral-400">
              Reply-To: {config.reply_to_email}
            </div>
          )}
          <div className="mt-2 font-mono text-xs text-neutral-400 dark:text-neutral-500">
            Password {config.password_masked}
          </div>
        </>
      ) : (
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          The platform From address appears here once an account is saved.
        </div>
      )}
    </CardShell>
  );
}

function HealthCard({
  config,
  loading,
}: {
  config: EmailTransportConfigMasked | null;
  loading: boolean;
}) {
  const status = config?.health_status ?? 'UNKNOWN';
  const gradient =
    status === 'OK'
      ? 'from-success-500 to-teal-500'
      : status === 'FAILING'
        ? 'from-danger-500 to-warning-500'
        : 'from-neutral-400 to-neutral-500';
  const checkedAt = formatStamp(config?.health_checked_at);
  return (
    <CardShell icon="HLTH" gradient={gradient} label="Delivery health">
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : (
        <>
          <div className="text-lg font-bold text-neutral-900 dark:text-white">
            {status === 'OK' ? 'Healthy' : status === 'FAILING' ? 'Failing' : 'Not checked yet'}
          </div>
          {config?.health_error && status === 'FAILING' && (
            <div className="mt-2 break-all rounded-md bg-danger-50 p-2 font-mono text-[11px] text-danger-700 dark:bg-danger-900/30 dark:text-danger-300">
              {config.health_error}
            </div>
          )}
          <div className="mt-2 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
            {checkedAt ? `Checked ${checkedAt}` : 'Checked daily at 5:00 AM IST'}
            {config && config.consecutive_failures > 0
              ? ` · ${config.consecutive_failures} consecutive failures`
              : ''}
          </div>
        </>
      )}
    </CardShell>
  );
}

function LastTestCard({
  config,
  loading,
}: {
  config: EmailTransportConfigMasked | null;
  loading: boolean;
}) {
  const testedAt = formatStamp(config?.last_tested_at);
  return (
    <CardShell icon="TEST" gradient="from-brand-500 to-indigo-500" label="Last test email">
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : (
        <>
          <div className="text-lg font-bold text-neutral-900 dark:text-white">
            {config?.last_test_status ?? 'Never'}
          </div>
          <div className="mt-1 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
            {testedAt ?? 'Send one once the account is verified.'}
          </div>
          {config?.last_test_error && (
            <div className="mt-2 break-all rounded-md bg-danger-50 p-2 font-mono text-[11px] text-danger-700 dark:bg-danger-900/30 dark:text-danger-300">
              {config.last_test_error}
            </div>
          )}
        </>
      )}
    </CardShell>
  );
}

function EmailGlyph() {
  return (
    <svg
      className="hidden h-14 w-14 text-white/80 md:block"
      fill="none"
      viewBox="0 0 48 48"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 12h36a2 2 0 012 2v20a2 2 0 01-2 2H6a2 2 0 01-2-2V14a2 2 0 012-2z" />
      <path d="M6 14l18 14L42 14" />
      <path d="M24 40v4M18 44h12" />
    </svg>
  );
}
